import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banner,
  Breadcrumbs,
  Button,
  Input,
  Label,
  LayerCard,
  Loader,
  Select,
  Text,
} from '@cloudflare/kumo'
import { HouseIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { getPrinter, startOnboarding, type PrinterDto } from '../api/printers'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { useTranslation } from '../i18n'
import type { Building } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  PrinterOnboardingWizard                                            */
/*                                                                     */
/*  Multi-stage onboarding flow:                                       */
/*    1. form             — admin enters name + building               */
/*    2. opening_popup    — starting OAuth, waiting for backend `start`*/
/*    3. awaiting_oauth   — popup is open, listening for postMessage   */
/*    4. connected        — Epson OK; backend self-test in flight      */
/*    5. verified         — printer reached `active`; CTA to detail    */
/*    6. error            — any failure; user can retry from scratch   */
/*                                                                     */
/*  postMessage contract (from apps/api/src/routes/printerOauthCallback*/
/*  .ts):                                                              */
/*    { type: 'print-center-oauth-result',                             */
/*      payload: { ok: true, printerId, productName, serialNumber }    */
/*               | { ok: false, code, message } }                      */
/* ------------------------------------------------------------------ */

type Stage =
  | { kind: 'form' }
  | { kind: 'opening_popup' }
  | { kind: 'awaiting_oauth'; printerId: string }
  | { kind: 'connected'; printerId: string; productName: string; serialNumber: string }
  | { kind: 'verified'; printerId: string }
  | { kind: 'error'; message: string }

const POPUP_W = 520
const POPUP_H = 700
const VERIFY_POLL_INTERVAL_MS = 3_000
const VERIFY_POLL_DEADLINE_MS = 60_000
/**
 * How often we check whether the OAuth popup was closed by the user. If
 * the popup closes without a successful postMessage we surface a friendly
 * error with a retry CTA — otherwise the wizard would hang on
 * `awaiting_oauth` forever (F3).
 */
const POPUP_CLOSED_POLL_MS = 500

/**
 * Allowed origin for postMessage replies from the OAuth callback page.
 * Computed from VITE_API_URL so dev/staging/prod all work without code
 * change. Falls back to the page origin if the env is empty (e.g. local
 * preview pointing at the same origin as the SPA).
 */
const API_ORIGIN = (() => {
  try {
    const raw = import.meta.env.VITE_API_URL as string | undefined
    if (!raw) return window.location.origin
    return new URL(raw).origin
  } catch {
    return window.location.origin
  }
})()

interface OauthOkPayload {
  ok: true
  printerId: string
  productName: string
  serialNumber: string
}

interface OauthErrPayload {
  ok: false
  code: string
  message: string
}

type OauthPayload = OauthOkPayload | OauthErrPayload

interface OauthMessage {
  type?: string
  payload?: OauthPayload
}

export function PrinterOnboardingWizard() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [buildingId, setBuildingId] = useState('')
  const [buildings, setBuildings] = useState<Building[]>([])
  const [buildingsError, setBuildingsError] = useState<string | null>(null)
  const [stage, setStage] = useState<Stage>({ kind: 'form' })

  const popupRef = useRef<Window | null>(null)
  const pollTimerRef = useRef<number | null>(null)
  // F3: separate timer that watches popup.closed. Cleared on successful
  // postMessage receipt, on unmount, and when the user navigates away.
  const popupClosedTimerRef = useRef<number | null>(null)
  // Tracks whether the OAuth popup posted a result before closing. Set on
  // any message receipt (success or error) so the popup-closed watchdog
  // doesn't flip an already-resolved wizard into the error state.
  const oauthSettledRef = useRef(false)

  // Load buildings for the picker — same /api/buildings endpoint used by
  // CompanyFormPage/BuildingsPage.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const list = await apiRequest<Building[]>('/api/buildings', { token })
        if (!cancelled) setBuildings(list)
      } catch (err) {
        if (!cancelled) {
          setBuildingsError(err instanceof ApiError ? err.message : t('printers.errors.generic'))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, t])

  // postMessage listener — the OAuth callback HTML posts here when the
  // popup completes. We accept only same-origin (== API origin) messages
  // with the documented `type` discriminator.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== API_ORIGIN) return
      const data = event.data as OauthMessage | undefined
      if (!data || data.type !== 'print-center-oauth-result' || !data.payload) return
      // Mark the OAuth flow as settled BEFORE the popup-closed watchdog
      // gets a chance to fire — otherwise a fast-closing popup could flip
      // us to the error state after a successful payload.
      oauthSettledRef.current = true
      if (popupClosedTimerRef.current !== null) {
        window.clearInterval(popupClosedTimerRef.current)
        popupClosedTimerRef.current = null
      }
      if (data.payload.ok) {
        setStage({
          kind: 'connected',
          printerId: data.payload.printerId,
          productName: data.payload.productName,
          serialNumber: data.payload.serialNumber,
        })
      } else {
        const code = data.payload.code
        const i18nKey = `printers.errors.${code}`
        const localized = t(i18nKey)
        setStage({
          kind: 'error',
          message: localized !== i18nKey ? localized : data.payload.message,
        })
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [t])

  // Verification poll — once OAuth succeeded, poll the printer row until
  // it reaches `active`, the deadline is hit, or self-test errored.
  const startVerificationPoll = useCallback(
    (printerId: string) => {
      const startedAt = Date.now()
      let cancelled = false

      const poll = async () => {
        if (cancelled) return
        try {
          const token = await getAccessTokenSilently()
          const printer: PrinterDto = await getPrinter(token, printerId)
          if (cancelled) return
          if (printer.status === 'active') {
            setStage({ kind: 'verified', printerId })
            return
          }
          if (printer.lastTestPrintError) {
            setStage({
              kind: 'error',
              message: t('printers.wizard.testFailed', { error: printer.lastTestPrintError }),
            })
            return
          }
        } catch {
          // ignore transient errors — keep polling until the deadline
        }
        if (Date.now() - startedAt > VERIFY_POLL_DEADLINE_MS) {
          // Stay on 'connected'; the user can manually open the detail
          // page via the Close button. This is intentional — we don't
          // want a deadline to flip the wizard to an error state because
          // the self-test may still complete eventually.
          return
        }
        pollTimerRef.current = window.setTimeout(
          () => void poll(),
          VERIFY_POLL_INTERVAL_MS,
        ) as unknown as number
      }

      void poll()

      return () => {
        cancelled = true
        if (pollTimerRef.current !== null) {
          window.clearTimeout(pollTimerRef.current)
          pollTimerRef.current = null
        }
      }
    },
    [getAccessTokenSilently, t],
  )

  useEffect(() => {
    if (stage.kind !== 'connected') return
    const stop = startVerificationPoll(stage.printerId)
    return stop
  }, [stage, startVerificationPoll])

  // F3: on unmount, tear down the popup-closed watchdog so the interval
  // doesn't leak if the admin navigates away mid-flow.
  useEffect(() => {
    return () => {
      if (popupClosedTimerRef.current !== null) {
        window.clearInterval(popupClosedTimerRef.current)
        popupClosedTimerRef.current = null
      }
    }
  }, [])

  const resetForm = () => setStage({ kind: 'form' })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || !buildingId) return
    setStage({ kind: 'opening_popup' })
    try {
      const token = await getAccessTokenSilently()
      const { printerId, authUrl } = await startOnboarding(token, {
        name: trimmed,
        buildingId,
      })
      const left = window.screenX + (window.outerWidth - POPUP_W) / 2
      const top = window.screenY + (window.outerHeight - POPUP_H) / 2
      const features = `width=${POPUP_W},height=${POPUP_H},left=${left},top=${top}`
      // The popup MUST be opened synchronously from this user-gesture
      // handler — otherwise Chrome/Safari blocks it.
      const popup = window.open(authUrl, 'print-center-oauth', features)
      if (!popup) {
        setStage({ kind: 'error', message: t('printers.wizard.popupBlocked') })
        return
      }
      popupRef.current = popup
      oauthSettledRef.current = false
      // F3: watch for the user closing the popup without completing
      // OAuth. window.close() in the callback HTML also trips this, but
      // by then `oauthSettledRef` is true so we no-op.
      if (popupClosedTimerRef.current !== null) {
        window.clearInterval(popupClosedTimerRef.current)
      }
      popupClosedTimerRef.current = window.setInterval(() => {
        const p = popupRef.current
        if (!p || p.closed) {
          if (popupClosedTimerRef.current !== null) {
            window.clearInterval(popupClosedTimerRef.current)
            popupClosedTimerRef.current = null
          }
          popupRef.current = null
          if (!oauthSettledRef.current) {
            setStage({ kind: 'error', message: t('printers.wizard.popupClosed') })
          }
        }
      }, POPUP_CLOSED_POLL_MS) as unknown as number
      setStage({ kind: 'awaiting_oauth', printerId })
    } catch (err) {
      let msg = t('printers.errors.generic')
      if (err instanceof ApiError) {
        const code = (err.body as { code?: string } | null)?.code
        const i18nKey = code ? `printers.errors.${code}` : ''
        const localized = i18nKey ? t(i18nKey) : ''
        if (localized && localized !== i18nKey) msg = localized
        else msg = err.message
      }
      setStage({ kind: 'error', message: msg })
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <button
              type="button"
              onClick={() => navigate('/admin/printers')}
              className="text-kumo-subtle hover:text-kumo-default text-sm"
            >
              {t('printers.title')}
            </button>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('printers.onboardPrinter')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('printers.onboardPrinter')}
        description={t('printers.onboardPageDescription')}
      />

      <LayerCard>
        <LayerCard.Primary>
          <div className="flex flex-col gap-4 max-w-2xl">
            {stage.kind === 'form' && (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {buildingsError && <Banner variant="error">{buildingsError}</Banner>}

                <div className="flex flex-col gap-1">
                  <Label>{t('printers.name')}</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('printers.wizard.namePlaceholder')}
                    maxLength={80}
                    required
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Label>{t('printers.wizard.buildingLabel')}</Label>
                  <Select
                    value={buildingId}
                    onValueChange={(v) => setBuildingId(v ?? '')}
                    items={[
                      { label: t('printers.wizard.buildingPlaceholder'), value: '' },
                      ...buildings.map((b) => ({ label: `${b.name} - ${b.city}`, value: b.id })),
                    ]}
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" variant="primary" disabled={!name.trim() || !buildingId}>
                    {t('printers.wizard.start')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/admin/printers')}
                  >
                    {t('printers.detail.cancel')}
                  </Button>
                </div>
              </form>
            )}

            {stage.kind === 'opening_popup' && (
              <div className="flex items-center gap-3">
                <Loader />
                <Text>{t('printers.wizard.connectingToEpson')}</Text>
              </div>
            )}

            {stage.kind === 'awaiting_oauth' && (
              <Banner variant="default">{t('printers.wizard.awaitingOAuth')}</Banner>
            )}

            {stage.kind === 'connected' && (
              <div className="flex flex-col gap-3">
                <Banner variant="default">
                  {t('printers.wizard.connected', {
                    product: stage.productName,
                    serial: stage.serialNumber,
                  })}
                </Banner>
                <div className="flex items-center gap-3">
                  <Loader />
                  <Text>{t('printers.wizard.sendingTestPage')}</Text>
                </div>
                <div>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(`/admin/printers/${stage.printerId}`)}
                  >
                    {t('printers.wizard.closeAndVerifyLater')}
                  </Button>
                </div>
              </div>
            )}

            {stage.kind === 'verified' && (
              <div className="flex flex-col gap-3">
                <Banner variant="default">{t('printers.wizard.verified')}</Banner>
                <div>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/admin/printers/${stage.printerId}`)}
                  >
                    {t('printers.wizard.viewPrinter')}
                  </Button>
                </div>
              </div>
            )}

            {stage.kind === 'error' && (
              <div className="flex flex-col gap-3">
                <Banner variant="error">{stage.message}</Banner>
                <div className="flex gap-2">
                  <Button variant="primary" onClick={resetForm}>
                    {t('printers.wizard.tryAgain')}
                  </Button>
                  <Button variant="secondary" onClick={() => navigate('/admin/printers')}>
                    {t('printers.backToList')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </LayerCard.Primary>
      </LayerCard>
    </div>
  )
}
